import {forwardRef, InputHTMLAttributes, ReactNode} from 'react'
import {Checkbox as CheckboxPrimitive} from '@base-ui/react/checkbox'
import {CheckIcon} from '@components/ui/icon'
import {InputHelpText} from '@components/ui/input-help-text'
import {cn} from '@utilities/helpers'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
    id: string
    label?: ReactNode
    labelPosition?: 'left' | 'right'
    errorMessage?: string
    helpText?: string
    containerClass?: string
    className?: string
    onCheckedChange?: (checked: boolean) => void
}

/**
 * Checkbox built on Base UI's Checkbox primitive, styled to match the legacy
 * `@components/commons/checkbox` aesthetic. Keeps the legacy DOM-style API
 * (`checked` + `onChange` with `event.target.checked`) so existing callers work.
 *
 * States:
 * - unchecked — `neutral-300` border on white
 * - hovered — `primary-300` border + focus ring (`xs` shadow + `primary-100`)
 * - checked — `primary-600` border on `primary-50` background, `primary-600` check icon
 * - disabled — `neutral-300` border on `neutral-100` background
 * - disabled + checked — `neutral-300` border/check on `neutral-100` background
 *
 * @example
 * <Checkbox id='terms' label='Accept terms' checked={value} onChange={e => set(e.target.checked)} />
 * <Checkbox id='news' label='Subscribe' labelPosition='left' helpText='Weekly digest' />
 */
const Checkbox = forwardRef<HTMLElement, CheckboxProps>(function Checkbox(
    {
        id,
        label,
        labelPosition = 'right',
        errorMessage,
        helpText,
        containerClass,
        className,
        checked,
        defaultChecked,
        disabled,
        name,
        onCheckedChange,
        onBlur
    },
    ref
) {
    return (
        <div className={cn('flex', label && 'gap-2', containerClass)}>
            <div
                className={cn(
                    'flex w-max max-w-full gap-1',
                    labelPosition === 'right' ? 'flex-row' : 'flex-row-reverse'
                )}
            >
                <CheckboxPrimitive.Root
                    ref={ref}
                    id={id}
                    name={name}
                    checked={checked}
                    defaultChecked={defaultChecked}
                    disabled={disabled}
                    onCheckedChange={onCheckedChange}
                    onBlur={onBlur as CheckboxPrimitive.Root.Props['onBlur']}
                    className={cn(
                        'flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-md border border-neutral-300 bg-white outline-none transition',
                        'hover:not-data-disabled:border-primary-300 hover:not-data-disabled:shadow-[var(--shadow-xs),0px_0px_0px_2px_var(--color-primary-100)]',
                        'data-checked:border-primary-600 data-checked:bg-primary-50',
                        'data-disabled:cursor-not-allowed data-disabled:border-neutral-300 data-disabled:bg-neutral-100',
                        className
                    )}
                    data-slot="checkbox"
                >
                    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-primary-600 data-disabled:text-neutral-300">
                        <CheckIcon className="size-3.5" />
                    </CheckboxPrimitive.Indicator>
                </CheckboxPrimitive.Root>
            </div>
            <div className="flex flex-col gap-2">
                {label && (
                    <label htmlFor={id} className="text-neutral-900 text-sm [&>a]:cursor-pointer [&>a]:underline">
                        {label}
                    </label>
                )}
                <InputHelpText helpText={helpText} error={errorMessage} />
            </div>
        </div>
    )
})

Checkbox.displayName = 'Checkbox'

export {Checkbox}
